import { useEffect, useMemo, useState } from 'preact/hooks';
import { useI18n } from '../i18n';
import {
  cancelMyContribution,
  commitMyContribution,
  createMyContribution,
  listMyContributions,
  type Contribution,
} from '../lib/admin';

/// Contributions log embedded inside PartnerSelf. Self-contained state —
/// it fetches its own list and articles list (the latter only when the
/// partner clicks "Add article stock"). Pass `as` to act against another
/// partner's contributions when signed in as admin.
export function PartnerContributions({
  accessToken,
  as,
  remainingLitres,
}: {
  accessToken: string;
  as?: number;
  /// Live `partners.remaining_litres` from the parent partner row, or
  /// null when the venue isn't tracking a litres budget. Used to render
  /// the consumed/available stats — we don't refetch it here because
  /// contributions don't auto-mutate it (only voucher redemptions do).
  remainingLitres?: number | null;
}) {
  const { t, locale } = useI18n();
  const [list, setList] = useState<Contribution[] | null>(null);
  const [adding, setAdding] = useState<'litres' | null>(null);
  const [confirming, setConfirming] = useState<Contribution | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    setErr(null);
    try {
      const items = await listMyContributions(accessToken, as);
      setList(items);
    } catch (e: any) {
      setErr(e?.message || t('partner.contributions.list_failed'));
    }
  }

  useEffect(() => { load(); }, [accessToken, as]);

  // Headline litres totals shown above the log. `contributed` counts only
  // committed entries — pending rows are still in their 24h cancel window
  // and don't get folded into the partner's pledged total yet (mirrors
  // the backend's contribution_litres semantics). `consumed` is the
  // gap between the cumulative pledge and the live remaining counter;
  // we clamp to 0 in case remaining_litres was manually bumped above the
  // logged contributions (admin edits, legacy seed, etc).
  const totalContributed = useMemo(() => {
    if (!list) return 0;
    return list
      .filter((c) => c.kind === 'litres' && c.state === 'committed')
      .reduce((sum, c) => sum + (c.litres ?? 0), 0);
  }, [list]);
  const hasLitresTracking = remainingLitres != null;
  const consumed = hasLitresTracking
    ? Math.max(0, totalContributed - (remainingLitres ?? 0))
    : null;

  async function onCancel(id: number) {
    if (!confirm(t('partner.contributions.cancel_confirm'))) return;
    try {
      await cancelMyContribution(accessToken, id, as);
      await load();
    } catch (e: any) {
      alert(e?.message || t('partner.contributions.cancel_failed'));
    }
  }

  async function onConfirmCommit(c: Contribution) {
    try {
      await commitMyContribution(accessToken, c.id, as);
      setConfirming(null);
      await load();
    } catch (e: any) {
      alert(e?.message || t('partner.contributions.commit_failed'));
    }
  }

  return (
    <section class="partner-card">
      <header class="partner-card-head">
        <h2 class="partner-card-title">{t('partner.contributions.title')}</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button class="partner-primary" onClick={() => setAdding('litres')}>
            {t('partner.contributions.add_litres')}
          </button>
        </div>
      </header>
      <p class="partner-muted">{t('partner.contributions.lede')}</p>

      <dl class="partner-stats" aria-label={t('partner.contributions.title')}>
        <div class="partner-stat">
          <dt>{t('partner.contributions.stats_contributed')}</dt>
          <dd>{formatLitres(totalContributed, locale)}</dd>
        </div>
        <div class="partner-stat">
          <dt>{t('partner.contributions.stats_consumed')}</dt>
          <dd>
            {consumed == null
              ? t('partner.contributions.stats_untracked')
              : formatLitres(consumed, locale)}
          </dd>
        </div>
        <div class="partner-stat">
          <dt>{t('partner.contributions.stats_available')}</dt>
          <dd>
            {hasLitresTracking
              ? formatLitres(remainingLitres ?? 0, locale)
              : t('partner.contributions.stats_untracked')}
          </dd>
        </div>
      </dl>
      <p class="partner-muted partner-stats-help">
        {t('partner.contributions.stats_help')}
      </p>

      {err && <p class="partner-error">{err}</p>}
      {!list && !err && <p class="partner-muted">{t('partner.loading')}</p>}

      {list && list.length === 0 && (
        <p class="partner-muted">{t('partner.contributions.none')}</p>
      )}

      {list && list.length > 0 && (
        <div class="partner-table-wrap">
          <table class="partner-table">
            <thead>
              <tr>
                <th>{t('partner.contributions.th_when')}</th>
                <th>{t('partner.contributions.th_what')}</th>
                <th>{t('partner.contributions.th_state')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <ContributionRow
                  key={c.id}
                  c={c}
                  locale={locale}
                  onCancel={() => onCancel(c.id)}
                  onCommit={() => setConfirming(c)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding === 'litres' && (
        <LitresModal
          accessToken={accessToken}
          as={as}
          onClose={() => setAdding(null)}
          onSaved={async () => { setAdding(null); await load(); }}
        />
      )}
      {confirming && (
        <ConfirmCommitModal
          c={confirming}
          locale={locale}
          onClose={() => setConfirming(null)}
          onConfirm={() => onConfirmCommit(confirming)}
        />
      )}
    </section>
  );
}

function ConfirmCommitModal({
  c, locale, onClose, onConfirm,
}: {
  c: Contribution;
  locale: string;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
}) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const summary = c.kind === 'litres'
    ? t('partner.contributions.entry_litres').replace('{n}', String(c.litres ?? 0))
    : t('partner.contributions.entry_stock')
        .replace('{n}', String(c.stockCount ?? 0))
        .replace('{article}', c.articleTitle ?? `#${c.articleId}`);
  return (
    <div class="partner-overlay" onClick={() => !busy && onClose()}>
      <div class="partner-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <h2 class="partner-card-title">{t('partner.contributions.commit_confirm_title')}</h2>
        <p class="partner-muted">{t('partner.contributions.commit_confirm_body')}</p>
        <p class="partner-mono" style={{ marginTop: 8 }}>
          {summary}
          {c.note && <span class="partner-row-sub"> — {c.note}</span>}
        </p>
        <p class="partner-muted" style={{ fontSize: 12, marginTop: 12 }}>
          {t('partner.contributions.commit_confirm_warn')}
        </p>
        <div class="partner-modal-actions">
          <button type="button" class="partner-link" disabled={busy} onClick={onClose}>
            {t('partner.contributions.commit_confirm_cancel')}
          </button>
          <button
            type="button"
            class="partner-primary"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try { await onConfirm(); } finally { setBusy(false); }
            }}
          >
            {busy ? t('partner.contributions.saving') : t('partner.contributions.commit_confirm_yes')}
          </button>
        </div>
        {/* Show formatted-when as a footer hint so the partner knows what they're confirming */}
        <p class="partner-muted" style={{ fontSize: 11, marginTop: 8 }}>
          {formatWhen(c.createdAt, locale)}
        </p>
      </div>
    </div>
  );
}

function ContributionRow({
  c,
  locale,
  onCancel,
  onCommit,
}: {
  c: Contribution;
  locale: string;
  onCancel: () => void;
  onCommit: () => void;
}) {
  const { t } = useI18n();
  const when = useMemo(() => formatWhen(c.createdAt, locale), [c.createdAt, locale]);
  const entry = c.kind === 'litres'
    ? t('partner.contributions.entry_litres').replace('{n}', String(c.litres ?? 0))
    : t('partner.contributions.entry_stock')
        .replace('{n}', String(c.stockCount ?? 0))
        .replace('{article}', c.articleTitle ?? `#${c.articleId}`);
  const stateLabel =
    c.state === 'pending'   ? t('partner.contributions.state_pending')
    : c.state === 'committed' ? t('partner.contributions.state_committed')
    : t('partner.contributions.state_cancelled');
  return (
    <tr class={c.state === 'cancelled' ? 'is-dim' : ''}>
      <td class="partner-mono">{when}</td>
      <td>
        {entry}
        {c.note && <div class="partner-row-sub">{c.note}</div>}
      </td>
      <td>{stateLabel}</td>
      <td>
        <div class="partner-row-actions">
          {c.state === 'pending' && (
            <button class="partner-link" onClick={onCommit}>
              {t('partner.contributions.commit_now')}
            </button>
          )}
          {c.cancellable && (
            <button class="partner-link partner-link--danger" onClick={onCancel}>
              {t('partner.contributions.cancel')}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function formatWhen(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatLitres(n: number, locale: string): string {
  try {
    const fmt = new Intl.NumberFormat(locale, {
      maximumFractionDigits: n % 1 === 0 ? 0 : 1,
    }).format(n);
    return `${fmt} L`;
  } catch {
    return `${n} L`;
  }
}

function LitresModal({
  accessToken, as, onClose, onSaved,
}: {
  accessToken: string;
  as?: number;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const { t } = useI18n();
  const [litres, setLitres] = useState('');
  const [note, setNote] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(e: Event) {
    e.preventDefault();
    setErr(null);
    setSaving(true);
    try {
      const n = Number(litres);
      if (!Number.isFinite(n) || n <= 0) {
        throw new Error(t('partner.contributions.litres_required'));
      }
      await createMyContribution(accessToken, {
        kind: 'litres',
        litres: n,
        note: note.trim() || undefined,
      }, as);
      await onSaved();
    } catch (e: any) {
      setErr(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="partner-overlay" onClick={onClose}>
      <form class="partner-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h2 class="partner-card-title">{t('partner.contributions.modal_litres_title')}</h2>
        <p class="partner-muted">{t('partner.contributions.litres_help')}</p>

        <label class="partner-field">
          <span>{t('partner.contributions.litres_label')} *</span>
          <input
            inputMode="decimal"
            value={litres}
            onInput={(e) => setLitres((e.target as HTMLInputElement).value)}
            placeholder={t('partner.contributions.litres_placeholder')}
            required
          />
        </label>

        <label class="partner-field">
          <span>{t('partner.contributions.note')}</span>
          <input
            value={note}
            onInput={(e) => setNote((e.target as HTMLInputElement).value)}
            placeholder={t('partner.contributions.note_placeholder')}
          />
        </label>

        {err && <p class="partner-error">{err}</p>}

        <div class="partner-modal-actions">
          <button type="button" class="partner-link" onClick={onClose}>
            {t('partner.articles.cancel')}
          </button>
          <button type="submit" class="partner-primary" disabled={saving}>
            {saving ? t('partner.contributions.saving') : t('partner.contributions.add')}
          </button>
        </div>
      </form>
    </div>
  );
}

